import { Component, OnChanges, Input } from '@angular/core';
import { Router } from '@angular/router';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexGrid,
  ApexPlotOptions,
  ApexAnnotations,
  ApexTooltip,
  ApexDataLabels,
} from 'ng-apexcharts';

/**
 * Each `dataList` row arrives in the shape produced by TimelineHelper:
 * ['', name, color, startDate, endDate]. The leading '' is a legacy row-label
 * slot. The tooltip is rendered from these plain values (see tooltipHtml()).
 */
const NAME = 1;
const COLOR = 2;
const START = 3;
const END = 4;

@Component({
    selector: 'app-timeline',
    templateUrl: './timeline.component.html',
    styleUrls: ['./timeline.component.scss'],
    standalone: false
})
export class TimelineComponent implements OnChanges {
  @Input() dataList: Array<any>;
  @Input() dateFormat: string;
  @Input() colors: Array<string>;

  hasData: boolean;
  currentRoute: string;
  errorMessage: string = 'There are no events for this harvest.';

  // ApexCharts config — bound piecemeal in the template via <apx-chart>.
  series: ApexAxisChartSeries = [];
  chart: ApexChart = {
    type: 'rangeBar',
    height: 140,
    fontFamily: 'inherit',
    toolbar: { show: false },
    animations: { enabled: false },
    // Lock the chart in place when the user scrolls. The real culprit (found
    // by instrumenting the live chart) was pan/zoom: a rangeBar on a datetime
    // axis is `apexcharts-zoomable` by default, so a trackpad scroll gesture
    // over the chart PANS the date axis and the bars slide out of view.
    // Disabling zoom + selection stops that. (The resize flags below are a
    // belt-and-suspenders guard against Apex's separate redraw-on-resize.)
    zoom: { enabled: false },
    selection: { enabled: false },
    redrawOnParentResize: false,
    redrawOnWindowResize: false,
  };
  plotOptions: ApexPlotOptions = {
    bar: {
      horizontal: true,
      borderRadius: 5,
      barHeight: '70%',
      rangeBarGroupRows: true,
      distributed: true,
    },
  };
  xaxis: ApexXAxis = {
    type: 'datetime',
    position: 'bottom',
    labels: {
      datetimeUTC: false,
      style: { colors: '#6b7280', fontSize: '12px' },
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  };
  yaxis: ApexYAxis = { show: false };
  grid: ApexGrid = {
    borderColor: '#eef1ee',
    xaxis: { lines: { show: true } },
    yaxis: { lines: { show: false } },
    padding: { left: 12, right: 12, top: 0, bottom: 0 },
  };
  dataLabels: ApexDataLabels = {
    enabled: true,
    formatter: (_val, opts) => opts.w.globals.labels[opts.dataPointIndex],
    textAnchor: 'start',
    offsetX: 8,
    style: { colors: ['#ffffff'], fontSize: '12px', fontWeight: 600 },
  };
  annotations: ApexAnnotations = {};
  tooltip: ApexTooltip = {
    // Let the card follow the hovered bar so each harvest's tooltip appears
    // next to it (the chart is now tall enough that it no longer clips).
    custom: ({ seriesIndex, dataPointIndex, w }) => {
      const point = w.config.series[seriesIndex].data[dataPointIndex];
      if (!point) { return ''; }
      return this.tooltipHtml(point.x, point.y[0], point.y[1]);
    },
  };
  fillColors: Array<string> = [];

  constructor(private router: Router) {
    this.currentRoute = this.router.url;

    if (this.currentRoute == '/console/harvests') {
      this.errorMessage = 'There are no current or future harvests to display.';
    }
  }

  ngOnChanges(): void {
    if (this.dataList && this.dataList.length > 0) {
      this.hasData = true;
      this.buildChart(this.dataList);
    } else {
      this.hasData = false;
    }
  }

  private buildChart(rows: Array<any>): void {
    const points = rows.map(row => ({
      x: row[NAME],
      y: [new Date(row[START]).getTime(), new Date(row[END]).getTime()],
      fillColor: row[COLOR] || '#0066ff',
    }));

    this.series = [{ name: 'Timeline', data: points }];
    this.fillColors = points.map(p => p.fillColor);

    // Size the chart to the number of rows so bars never crowd or stretch.
    // ~34px/row keeps the bars compact; the 140 floor covers the axis + a
    // couple of rows so a near-empty chart still looks intentional.
    this.chart = { ...this.chart, height: Math.max(140, points.length * 34 + 50) };

    // "Today" marker — a single native annotation replaces the old overlay div.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.annotations = {
      xaxis: [{
        x: today.getTime(),
        strokeDashArray: 4,
        borderColor: '#16a34a',
        label: {
          text: 'Today',
          orientation: 'horizontal',
          position: 'top',
          borderColor: '#16a34a',
          style: { color: '#fff', background: '#16a34a', fontSize: '11px' },
        },
      }],
    };
  }

  /** Builds the hover card from a bar's own values — no pre-baked HTML in the data. */
  private tooltipHtml(name: string, startMs: number, endMs: number): string {
    const fmt = (ms: number) =>
      new Date(ms).toLocaleDateString('en-us', { month: 'short', day: 'numeric' });
    const days = Math.max(0, Math.round((endMs - startMs) / 86400000));

    return `
      <div class="tt-card">
        <div class="tt-header"><strong>${name}</strong></div>
        <div class="tt-content">
          <p>${fmt(startMs)} - ${fmt(endMs)}</p>
          <p><strong>Duration:</strong> ${days} days</p>
        </div>
      </div>`;
  }
}
